import type { RegressionOutputSuccess } from '@aloft/toolkit-shared';
import { Flex, InfoIcon, SimpleSelect } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { RegressionScatterGraph } from '../../../components/graphs/regression/RegressionScatterGraph.tsx';
import type { RegressionScatterPoint } from '../../../components/graphs/regression/types.ts';
import { listingsToScatterData } from '../../../components/graphs/regression/utils.ts';

const GRAPH_HEIGHT = 300;

const formatInteger = (value: number) => Math.round(value).toString();

type RightGraphOption = {
  label: string;
  xKey: keyof RegressionScatterPoint;
  xLabel: string;
  xTickFormatter?: (value: number) => string;
  /** Set for discrete count fields so the x-axis never shows fractional ticks. */
  xAllowDecimals?: boolean;
};

const RIGHT_GRAPH_OPTIONS: RightGraphOption[] = [
  { label: 'Gross Living Area vs. Sale Price', xKey: 'gla', xLabel: 'GLA (sqft)' },
  { label: 'Site Size vs. Sale Price', xKey: 'siteSize', xLabel: 'Site Size (sqft)' },
  { label: 'Actual Age vs. Sale Price', xKey: 'actualAge', xLabel: 'Actual Age (yrs)' },
  {
    label: 'Bedroom Count vs. Sale Price',
    xKey: 'bedrooms',
    xLabel: 'Bedroom Count',
    xTickFormatter: formatInteger,
    xAllowDecimals: false,
  },
  {
    label: 'Full Bathrooms vs. Sale Price',
    xKey: 'fullBaths',
    xLabel: 'Full Bathrooms',
    xTickFormatter: formatInteger,
    xAllowDecimals: false,
  },
  {
    label: 'Half Bathrooms vs. Sale Price',
    xKey: 'halfBaths',
    xLabel: 'Half Bathrooms',
    xTickFormatter: formatInteger,
    xAllowDecimals: false,
  },
  {
    label: 'Parking Spaces vs. Sale Price',
    xKey: 'garageCarport',
    xLabel: 'Parking Spaces',
    xTickFormatter: formatInteger,
    xAllowDecimals: false,
  },
];

type RegressionModalGraphSectionProps = {
  output: RegressionOutputSuccess;
};

export const RegressionModalGraphSection = ({ output }: RegressionModalGraphSectionProps) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState('0');

  const data = listingsToScatterData(output.listings, output);

  const selectedOption =
    RIGHT_GRAPH_OPTIONS[Number(selectedOptionIndex)] ?? RIGHT_GRAPH_OPTIONS[0]!;

  return (
    <Flex flexDirection="column" gap="0.75rem">
      <Flex gap="1rem" alignItems="stretch">
        <Flex flexDirection="column" flex={1}>
          <RegressionScatterGraph
            data={data}
            xKey="predictedPrice"
            yKey="actualPrice"
            xLabel="Predicted Sale Price"
            yLabel="Actual Sale Price"
            disableAnimations={false}
            height={GRAPH_HEIGHT}
            showBestFitLine
          />
        </Flex>
        <Flex
          flexDirection="column"
          gap="0.5rem"
          flex={1}
          backgroundColor="graphBackground"
          borderRadius="8px"
        >
          <Flex alignItems="center" gap="0.5rem" padding="0.75rem" marginBottom="-20px">
            <SimpleSelect
              value={selectedOptionIndex}
              onChange={(e) => setSelectedOptionIndex(e.target.value)}
            >
              {RIGHT_GRAPH_OPTIONS.map((option, index) => (
                <option key={option.label} value={String(index)}>
                  {option.label}
                </option>
              ))}
            </SimpleSelect>
            <InfoIcon tooltipContent="Multivariate regression is necessary to account for the various characteristics that simultaneously influence a property's value." />
          </Flex>
          <RegressionScatterGraph
            data={data}
            xKey={selectedOption.xKey}
            yKey="actualPrice"
            xLabel={selectedOption.xLabel}
            yLabel="Sale Price"
            disableAnimations={false}
            height={GRAPH_HEIGHT}
            bare
            xTickFormatter={selectedOption.xTickFormatter}
            xAllowDecimals={selectedOption.xAllowDecimals}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
